import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ResetPasswordDialog } from "../components/ResetPasswordDialog";
import { BranchesAdmin, ResetDataCard, ChangeEmailCard } from "../components/AdminTools";

const EMPTY = { name: "", email: "", password: "", role: "employee", branch_id: "" };

export default function Employees() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [branches, setBranches] = useState([]);
  const bname = (id) => branches.find((b) => b.id === id)?.name;

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  const loadBranches = () => api.get("/branches").then((r) => setBranches(r.data));
  useEffect(() => { load(); loadBranches(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", form); toast.success(t("success")); setForm(EMPTY); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-4xl sm:text-5xl font-extrabold">{t("employees")}</h1>
      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <form onSubmit={submit} className="bg-white border rounded-lg p-5 space-y-4" data-testid="employee-form">
          <h3 className="font-bold flex items-center gap-2"><UserPlus className="w-4 h-4" />{t("addEmployee")}</h3>
          {[["name", "name", "text"], ["email", "email", "email"], ["password", "password", "password"]].map(([k, l, type]) => (
            <div key={k} className="space-y-1.5"><Label>{t(l)}</Label>
              <Input type={type} dir={k === "name" ? "auto" : "ltr"} data-testid={`employee-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required minLength={k === "password" ? 6 : undefined} /></div>
          ))}
          <div className="space-y-1.5"><Label>{t("role")}</Label>
            <select data-testid="employee-role-select" className="w-full h-9 border rounded-md px-2 bg-white text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="employee">{t("employee")}</option><option value="admin">{t("admin")}</option>
            </select></div>
          {form.role === "employee" && (
            <div className="space-y-1.5"><Label>{t("branch")}</Label>
              <select data-testid="employee-branch-select" required className="w-full h-9 border rounded-md px-2 bg-white text-sm" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">{t("selectBranch")}</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select></div>
          )}
          <Button type="submit" className="w-full" data-testid="employee-submit-btn">{t("add")}</Button>
        </form>
        <div className="bg-white border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{["name", "email", "role", "branch", "actions"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`employee-row-${u.email}`}>
                  <TableCell className="font-bold">{u.name}</TableCell>
                  <TableCell dir="ltr" className="text-start">{u.email}</TableCell>
                  <TableCell>{t(u.role)}</TableCell>
                  <TableCell data-testid={`employee-branch-${u.email}`}>{u.role === "admin" ? t("allBranches") : bname(u.branch_id) || "-"}</TableCell>
                  <TableCell className="flex gap-1">{u.id !== user.id && <ResetPasswordDialog target={u} />}{u.id !== user.id && <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`employee-delete-${u.email}`} onClick={() => remove(u.id)}><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <BranchesAdmin branches={branches} onDone={loadBranches} />
      <ChangeEmailCard />
      <ResetDataCard />
    </div>
  );
}
